import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface AppConfig {
  id: number;
  package_name: string;
  package_display_name: string;
  min_version: string;
  max_version: string;
  db_relative_path: string;
  source_table: string;
  shadow_table: string;
  captured_at_column: string;
  shortcut_icon_url?: string | null;
}

interface CreateAppConfigRequest {
  package_name: string;
  package_display_name: string;
  min_version: string;
  max_version: string;
  db_relative_path: string;
  source_table: string;
  shadow_table: string;
  captured_at_column: string;
  shortcut_icon_url: string;
}

interface AppConfigModalProps {
  config?: AppConfig;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateAppConfigRequest) => void;
  isLoading: boolean;
}

function appConfigToApiBody(data: CreateAppConfigRequest): Record<string, unknown> {
  const shortcut = data.shortcut_icon_url?.trim();
  return {
    package_name: data.package_name,
    package_display_name: data.package_display_name,
    min_version: data.min_version,
    max_version: data.max_version,
    db_relative_path: data.db_relative_path,
    source_table: data.source_table,
    shadow_table: data.shadow_table,
    captured_at_column: data.captured_at_column,
    shortcut_icon_url: shortcut ? shortcut : null,
  };
}

const AppConfigModal: React.FC<AppConfigModalProps> = ({ config, isOpen, onClose, onSave, isLoading }) => {
  const [formData, setFormData] = useState<CreateAppConfigRequest>(() => ({
    package_name: config?.package_name || '',
    package_display_name: config?.package_display_name || '',
    min_version: config?.min_version || '',
    max_version: config?.max_version || '',
    db_relative_path: config?.db_relative_path || '',
    source_table: config?.source_table || '',
    shadow_table: config?.shadow_table || 'shadow_storage',
    captured_at_column: config?.captured_at_column || 'captured_at',
    shortcut_icon_url: config?.shortcut_icon_url || '',
  }));

  React.useEffect(() => {
    if (config) {
      setFormData({
        package_name: config.package_name,
        package_display_name: config.package_display_name,
        min_version: config.min_version,
        max_version: config.max_version,
        db_relative_path: config.db_relative_path,
        source_table: config.source_table,
        shadow_table: config.shadow_table,
        captured_at_column: config.captured_at_column,
        shortcut_icon_url: config.shortcut_icon_url || '',
      });
    } else {
      setFormData({
        package_name: '',
        package_display_name: '',
        min_version: '',
        max_version: '',
        db_relative_path: '',
        source_table: '',
        shadow_table: 'shadow_storage',
        captured_at_column: 'captured_at',
        shortcut_icon_url: '',
      });
    }
  }, [config]);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{config ? 'Edit Configuration' : 'Create New Configuration'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="package_name">Package Name *</label>
              <input
                id="package_name"
                type="text"
                placeholder="e.g., com.zhiliaoapp.musically"
                value={formData.package_name}
                onChange={(e) => setFormData(prev => ({ ...prev, package_name: e.target.value }))}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="package_display_name">Display Name *</label>
              <input
                id="package_display_name"
                type="text"
                placeholder="e.g., TikTok"
                value={formData.package_display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, package_display_name: e.target.value }))}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="min_version">Min Version *</label>
              <input
                id="min_version"
                type="text"
                placeholder="e.g., 1.0.0"
                value={formData.min_version}
                onChange={(e) => setFormData(prev => ({ ...prev, min_version: e.target.value }))}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="max_version">Max Version *</label>
              <input
                id="max_version"
                type="text"
                placeholder="e.g., 999.999.999"
                value={formData.max_version}
                onChange={(e) => setFormData(prev => ({ ...prev, max_version: e.target.value }))}
                required
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="db_relative_path">DB Relative Path *</label>
              <input
                id="db_relative_path"
                type="text"
                placeholder="e.g., blackbox/data/user/0/com.example/databases/db.db"
                value={formData.db_relative_path}
                onChange={(e) => setFormData(prev => ({ ...prev, db_relative_path: e.target.value }))}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="source_table">Source Table *</label>
              <input
                id="source_table"
                type="text"
                placeholder="e.g., eventv3"
                value={formData.source_table}
                onChange={(e) => setFormData(prev => ({ ...prev, source_table: e.target.value }))}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="shadow_table">Shadow Table</label>
              <input
                id="shadow_table"
                type="text"
                placeholder="shadow_storage"
                value={formData.shadow_table}
                onChange={(e) => setFormData(prev => ({ ...prev, shadow_table: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label htmlFor="captured_at_column">Captured At Column</label>
              <input
                id="captured_at_column"
                type="text"
                placeholder="captured_at"
                value={formData.captured_at_column}
                onChange={(e) => setFormData(prev => ({ ...prev, captured_at_column: e.target.value }))}
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="shortcut_icon_url">Home screen shortcut icon URL</label>
              <input
                id="shortcut_icon_url"
                type="url"
                placeholder="https://cdn.example.com/icons/tiktok.png"
                value={formData.shortcut_icon_url}
                onChange={(e) => setFormData(prev => ({ ...prev, shortcut_icon_url: e.target.value }))}
              />
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.45)' }}>
                Optional. Used by the mobile app for launcher shortcuts (HTTPS URL to a PNG or similar).
              </p>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (config ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

const AppConfigManager: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedConfig, setSelectedConfig] = useState<AppConfig | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [collapsedApps, setCollapsedApps] = useState<Set<string>>(new Set());
  const [searchFilter, setSearchFilter] = useState('');

  const { data: configs = [], isLoading, error, refetch } = useQuery<AppConfig[]>({
    queryKey: ['app-configurations'],
    queryFn: async () => {
      const response = await fetch('/api/app-configurations');
      if (!response.ok) throw new Error('Failed to fetch app configurations');
      return response.json();
    },
    refetchInterval: 30000,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateAppConfigRequest) => {
      const response = await fetch('/api/app-configurations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appConfigToApiBody(data)),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create configuration');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedConfig(undefined);
      queryClient.invalidateQueries({ queryKey: ['app-configurations'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CreateAppConfigRequest }) => {
      const response = await fetch(`/api/app-configurations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appConfigToApiBody(data)),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update configuration');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedConfig(undefined);
      queryClient.invalidateQueries({ queryKey: ['app-configurations'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/app-configurations/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to delete configuration');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['app-configurations'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const handleCreate = useCallback(() => {
    setSelectedConfig(undefined);
    setIsModalOpen(true);
    setActionError(null);
  }, []);

  const handleEdit = useCallback((config: AppConfig) => {
    setSelectedConfig(config);
    setIsModalOpen(true);
    setActionError(null);
  }, []);

  const handleDelete = useCallback((config: AppConfig) => {
    if (window.confirm(`Delete configuration for "${config.package_name}" [${config.min_version} - ${config.max_version}]?`)) {
      deleteMutation.mutate(config.id);
    }
  }, [deleteMutation]);

  const handleSave = useCallback((data: CreateAppConfigRequest) => {
    if (selectedConfig) {
      updateMutation.mutate({ id: selectedConfig.id, data });
    } else {
      createMutation.mutate(data);
    }
  }, [selectedConfig, createMutation, updateMutation]);

  const toggleApp = useCallback((packageName: string) => {
    setCollapsedApps(prev => {
      const next = new Set(prev);
      if (next.has(packageName)) {
        next.delete(packageName);
      } else {
        next.add(packageName);
      }
      return next;
    });
  }, []);

  // Group configs by package_name
  const grouped: Record<string, AppConfig[]> = {};
  for (const config of configs) {
    if (searchFilter && !config.package_name.toLowerCase().includes(searchFilter.toLowerCase())) continue;
    if (!grouped[config.package_name]) grouped[config.package_name] = [];
    grouped[config.package_name].push(config);
  }
  const packageNames = Object.keys(grouped).sort();

  if (isLoading) return <div className="loading">Loading app configurations...</div>;
  if (error) return <div className="error">Error loading configurations: {(error as Error).message}</div>;

  return (
    <div className="region-manager">
      <div className="page-header">
        <h1>App Configurations</h1>
        <div className="page-actions">
          <button onClick={() => refetch()} className="btn-secondary">Refresh</button>
          <button onClick={handleCreate} className="btn-primary">+ Add Configuration</button>
        </div>
      </div>

      {actionError && (
        <div className="error-banner">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)}>×</button>
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="Search by package name..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '10px 14px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.05)',
            color: 'white',
            fontSize: '14px',
          }}
        />
        {searchFilter && (
          <span style={{ marginLeft: '12px', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
            Found {packageNames.length} app(s)
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {packageNames.map(packageName => {
          const appConfigs = grouped[packageName];
          const isCollapsed = collapsedApps.has(packageName);
          return (
            <div key={packageName} className="group-card">
              <div
                className="group-header"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleApp(packageName)}
              >
                <div className="group-title">
                  <h3 style={{ fontSize: '15px' }}>
                    {appConfigs[0]?.package_display_name || packageName}
                    <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginLeft: '10px', fontWeight: 400 }}>{packageName}</span>
                  </h3>
                  <span className="group-count">{appConfigs.length} configuration{appConfigs.length !== 1 ? 's' : ''}</span>
                </div>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '18px' }}>
                  {isCollapsed ? '▸' : '▾'}
                </span>
              </div>

              {!isCollapsed && (
                <div style={{ padding: '0 16px 16px' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '13px',
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                        <th style={thStyle}>Version Range</th>
                        <th style={thStyle}>DB Path</th>
                        <th style={thStyle}>Source Table</th>
                        <th style={thStyle}>Shadow Table</th>
                        <th style={thStyle}>Captured At</th>
                        <th style={thStyle}>Shortcut icon</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appConfigs.map(config => (
                        <tr key={config.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                          <td style={tdStyle}>
                            <span style={{
                              background: 'rgba(99, 102, 241, 0.15)',
                              border: '1px solid rgba(99, 102, 241, 0.3)',
                              borderRadius: '4px',
                              padding: '2px 8px',
                              fontSize: '12px',
                              fontFamily: 'monospace',
                            }}>
                              {config.min_version} - {config.max_version}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '11px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={config.db_relative_path}>
                            {config.db_relative_path}
                          </td>
                          <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{config.source_table}</td>
                          <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{config.shadow_table}</td>
                          <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{config.captured_at_column}</td>
                          <td style={{ ...tdStyle, fontSize: '11px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {config.shortcut_icon_url ? (
                              <a
                                href={config.shortcut_icon_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{ color: 'rgba(129, 140, 248, 0.95)' }}
                                title={config.shortcut_icon_url}
                              >
                                {config.shortcut_icon_url.length > 40
                                  ? `${config.shortcut_icon_url.slice(0, 40)}…`
                                  : config.shortcut_icon_url}
                              </a>
                            ) : (
                              <span style={{ color: 'rgba(255,255,255,0.35)' }}>—</span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEdit(config); }}
                              className="btn-icon"
                              title="Edit"
                              style={{ marginRight: '4px' }}
                            >
                              ✏️
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDelete(config); }}
                              className="btn-icon btn-danger"
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {packageNames.length === 0 && configs.length > 0 && (
          <div className="empty-state">
            <h3>No apps match your search</h3>
            <button onClick={() => setSearchFilter('')} className="btn-secondary">Clear Search</button>
          </div>
        )}

        {configs.length === 0 && (
          <div className="empty-state">
            <h3>No app configurations yet</h3>
            <p>Add your first application configuration to define how events are captured.</p>
            <button onClick={handleCreate} className="btn-primary">+ Add First Configuration</button>
          </div>
        )}
      </div>

      <AppConfigModal
        config={selectedConfig}
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedConfig(undefined); setActionError(null); }}
        onSave={handleSave}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  color: 'rgba(255,255,255,0.6)',
  fontWeight: 500,
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  color: 'rgba(255,255,255,0.85)',
};

export default AppConfigManager;
